import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Search, User, BookOpen, Wand2, CreditCard, FileText, PenLine, Flag, Newspaper, Sparkles, ChevronRight } from 'lucide-react';
import ReportModal from './ReportModal';
import { apiGet, apiPost } from '../utils/apiUtils';
import { API_URLS } from '../config/api';
import { IS_EUROPE, BRAND, HAS_PAYMENTS, HAS_TELEGRAM, LANGUAGES, LANGUAGE, LANGUAGE_CODES, getLanguage, setLanguage, localePath } from '../config/region';
import { t } from '../i18n';
import './SiteHeader.css';

const NAV_LINKS = [
  { label: 'Поиск',           href: '/search',   Icon: Search },
  { label: 'Профиль',         href: '/user/me',  Icon: User },
  { label: 'Библиотека',      href: '/library',  Icon: BookOpen },
  { label: 'Создать новеллу', href: '/building',  Icon: Wand2 },
  { label: 'Оплата',          href: '/payment',  Icon: CreditCard },
  { label: 'Блог',            href: '/blog',     Icon: FileText },
  { label: 'Новости',         href: '/news',     Icon: Newspaper },
  { label: 'Стать автором',  href: '/authors',  Icon: PenLine },
]
  .filter(({ href }) => HAS_PAYMENTS || href !== '/payment')
  /* "Стать автором" points at /authors, which europe does not route — see App.js. */
  .filter(({ href }) => !IS_EUROPE || href !== '/authors');

/* Toast is shown at most once per browser session, and never on the news pages */
const TOAST_DISMISSED_KEY = 'newsToastDismissed';

const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [newsCount, setNewsCount] = useState(0);
  const [toast, setToast] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [language, setLanguageState] = useState(getLanguage);
  const sidebarRef = useRef(null);

  const ActiveFlag = (LANGUAGES.find(l => l.code === language) || LANGUAGES[0]).Flag;

  /* Reconcile the language with the account (europe only).
     The URL wins for the page on screen — it was already rendered under its prefix, and a shared
     link has to open in the language it names for everyone. So this no longer redirects; it only
     keeps the account and the stored fallback in step with the prefix being viewed, which is what
     an unprefixed entry point resolves against next time. */
  useEffect(() => {
    if (!IS_EUROPE) return;
    apiGet(API_URLS.USER_ME, {}, false, '', true)
      .then(res => {
        const server = res.data?.ui_language;
        if (server === LANGUAGE) return;
        /* Viewing a prefix that disagrees with the account is a deliberate act — following a link
           in another language. Treat the URL as the new choice and tell the backend, so authored
           text comes back in the language the reader is actually looking at. */
        setLanguage(LANGUAGE);
        setLanguageState(LANGUAGE);
        apiPost(API_URLS.USER_LANGUAGE, { language: LANGUAGE }, {}, false, '', true).catch(() => {});
      })
      .catch(() => {});
  }, []);

  /*
   * Switching language is a navigation, not a re-render: every page lives at a language-specific
   * URL, so the same page in German is a different address. Swapping the first path segment keeps
   * the reader where they are (/en/blog/... → /de/blog/...) and the full load that follows is also
   * what repaints authored text, which the backend renders server-side in the chosen language.
   */
  const applyLanguage = (code) => {
    setLanguage(code);
    setLangOpen(false);
    const rest = window.location.pathname.split('/').slice(2).join('/');
    const target = `/${LANGUAGE_CODES[code]}${rest ? `/${rest}` : ''}${window.location.search}`;
    apiPost(API_URLS.USER_LANGUAGE, { language: code }, {}, false, '', true)
      .catch(() => {})
      .finally(() => { window.location.href = target; });
  };

  /* Close on outside click */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  /* Prevent body scroll when sidebar open */
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  /* Unread news notification count — re-checked when the tab regains focus */
  useEffect(() => {
    const fetchCount = () => {
      apiGet(API_URLS.NEWS_UNREAD_COUNT, {}, false, '', true)
        .then(res => {
          const count = res.data?.count || 0;
          setNewsCount(count);
          const dismissed = sessionStorage.getItem(TOAST_DISMISSED_KEY);
          const onNews = window.location.pathname.startsWith('/news');
          if (count > 0 && !dismissed && !onNews) setToast(true);
        })
        .catch(() => {});
    };
    fetchCount();
    const onRead = () => { setNewsCount(0); setToast(false); };
    const onFocus = () => fetchCount();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchCount(); };
    window.addEventListener('newsRead', onRead);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('newsRead', onRead);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const dismissToast = () => {
    sessionStorage.setItem(TOAST_DISMISSED_KEY, '1');
    setToast(false);
  };

  return (
    <>
      <header className="sh">
        <div className="sh__brand-group">
          <a href={localePath('/')} className="sh__brand">{BRAND}</a>
          <span className="sh__tagline">{t('Открой свою вселенную')}</span>
        </div>
        {/* The bar is space-between with two children, so the flag goes inside the right-hand
            group — a third top-level child would spread across the row instead. */}
        <div className="sh__actions">
          {IS_EUROPE && (
            <button
              className="sh__menu-btn sh-lang-btn"
              onClick={() => setLangOpen(true)}
              aria-label="Change language"
            >
              <ActiveFlag className="sh-lang-flag" />
            </button>
          )}
          <button
            className="sh__menu-btn"
            onClick={() => setOpen(true)}
            aria-label={t('Открыть меню')}
          >
            <Menu size={22} />
            {newsCount > 0 && <span className="sh__menu-dot" aria-hidden="true" />}
          </button>
        </div>
      </header>

      {/* Unread-news toast */}
      {toast && (
        <div className="sh-toast" role="status">
          <a href={localePath('/news')} className="sh-toast__body" onClick={dismissToast}>
            <span className="sh-toast__cta">
              <Sparkles size={15} />
              {t('Читать')}
              <ChevronRight size={16} className="sh-toast__cta-arrow" />
            </span>
            <span className="sh-toast__text">
              <strong>{t('Важные новости')}{newsCount > 1 ? ` (${newsCount})` : ''}</strong>
              <span>{t('Нажмите, чтобы прочитать')}</span>
            </span>
          </a>
          <button
            className="sh-toast__close"
            onClick={dismissToast}
            aria-label={t('Закрыть уведомление')}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Overlay */}
      <div
        className={`sh-overlay${open ? ' sh-overlay--visible' : ''}`}
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`sh-sidebar${open ? ' sh-sidebar--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('Меню навигации')}
      >
        <div className="sh-sidebar-header">
          <span className="sh-sidebar-brand">{BRAND}</span>
          <button
            className="sh-sidebar-close"
            onClick={() => setOpen(false)}
            aria-label={t('Закрыть меню')}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sh-sidebar-nav">
          {NAV_LINKS.map(({ label, href, Icon }) => (
            <a
              key={href}
              href={href}
              className="sh-sidebar-link"
              onClick={() => setOpen(false)}
            >
              <Icon size={17} />
              {t(label)}
              {href === '/news' && newsCount > 0 && (
                <span className="sh-sidebar-badge">{newsCount > 99 ? '99+' : newsCount}</span>
              )}
            </a>
          ))}

          {HAS_TELEGRAM && (
          <a
            href="https://t.me/+4LsIU0kLAc1jN2My"
            target="_blank"
            rel="noopener noreferrer"
            className="sh-sidebar-link"
            onClick={() => setOpen(false)}
          >
            <svg style={{width:17,height:17,flexShrink:0}} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
            Telegram
          </a>
          )}

          <button
            className="sh-sidebar-link sh-sidebar-link--btn"
            onClick={() => { setOpen(false); setReportOpen(true); }}
          >
            <Flag size={17} />
            {t('Сообщить об ошибке / задать вопрос')}
          </button>
        </nav>
      </div>

      {reportOpen && <ReportModal onClose={() => setReportOpen(false)} />}

      {langOpen && (
        <div className="sh-lang-overlay" onClick={() => setLangOpen(false)}>
          <div className="sh-lang-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sh-lang-head">
              <span className="sh-lang-title">Language</span>
              <button className="sh-lang-close" onClick={() => setLangOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className="sh-lang-list">
              {LANGUAGES.map(({ code, label, Flag: LangFlag }) => (
                <button
                  key={code}
                  className={`sh-lang-option${code === language ? ' sh-lang-option--active' : ''}`}
                  onClick={() => applyLanguage(code)}
                >
                  <LangFlag className="sh-lang-flag" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteHeader;
